// One read-only presentation contract for print, PDF and outgoing messages.
// Never issues documents, applies payments, or changes accounting policy.
export const billingError = (code, message = 'بيانات المستند غير متاحة أو غير متطابقة؛ أعد تحميل المصدر.') => {
    const error = new Error(message);
    error.code = code;
    return error;
};
export const hasMoney = (value) => value !== null && value !== undefined && String(value).trim() !== '';
export const money = (value, field = 'amount') => {
    if (!hasMoney(value) || typeof value === 'boolean' || !Number.isFinite(Number(value)) || Number(value) < 0)
        throw billingError('DOCUMENT_AMOUNT_UNAVAILABLE', `قيمة ${field} غير متاحة؛ لا يمكن إصدار مبلغ تقديري.`);
    const cents = Math.round((Number(value) + Number.EPSILON) * 100);
    if (!Number.isSafeInteger(cents)) throw billingError('DOCUMENT_AMOUNT_UNAVAILABLE');
    return cents / 100;
};
export const customerBillingItems = (items) => {
    if (!Array.isArray(items)) throw billingError('DOCUMENT_ITEMS_UNAVAILABLE');
    return items.filter((item) => item && item.customerBillable !== false && item.customer_billable !== false)
        .map((item) => {
            const quantity = Number(item.quantity ?? item.qty ?? 1);
            if (!Number.isFinite(quantity) || quantity <= 0) throw billingError('DOCUMENT_QUANTITY_INVALID');
            const price = money(item.unit_price ?? item.unitPrice ?? item.price ?? item.amount, 'سعر البند');
            const discount = money(item.discount ?? 0, 'الخصم');
            const taxRate = money(item.vatRate ?? item.tax_rate ?? item.vat ?? 0, 'الضريبة');
            // Current released visit invoices explicitly have tax_total=0. Tax activation is a separate gate.
            if (taxRate !== 0) throw billingError('DOCUMENT_TAX_POLICY_UNAVAILABLE', 'الضريبة غير مفعلة في مسار المستند الحالي.');
            const subtotal = money(quantity * price);
            if (discount > subtotal) throw billingError('DOCUMENT_DISCOUNT_INVALID');
            const total = money(subtotal - discount);
            const supplied = item.total ?? item.line_total;
            if (hasMoney(supplied) && money(supplied) !== total) throw billingError('DOCUMENT_LINE_MISMATCH');
            return { ...item, description: item.description || item.name || item.itemName || 'بند',
                quantity, qty: quantity, price, unit_price: price, discount, subtotal, total, vatRate: 0 };
        });
};
export const resolveBillingDocument = (payload = {}) => {
    const settings = payload.settings || {};
    const docType = payload.doc_type || payload.docType || settings.doc_type || 'invoice';
    if (!['invoice', 'quote', 'diagnosis', 'receipt'].includes(docType)) throw billingError('DOCUMENT_TYPE_UNSUPPORTED');
    const items = customerBillingItems(payload.items);
    const subtotal = money(items.reduce((sum, item) => sum + item.subtotal, 0));
    const discount = money(items.reduce((sum, item) => sum + item.discount, 0));
    const total = money(items.reduce((sum, item) => sum + item.total, 0));
    const tax = money(settings.totals?.tax ?? payload.tax ?? 0);
    if (tax !== 0) throw billingError('DOCUMENT_TAX_POLICY_UNAVAILABLE');
    const approved = settings.final_customer_total ?? payload.final_customer_total ?? payload.approved_final_customer_total;
    const finalTotal = hasMoney(approved) ? money(approved, 'الإجمالي المعتمد') : null;
    if (finalTotal !== null && finalTotal !== total) throw billingError('DOCUMENT_TOTAL_MISMATCH', 'تفاصيل المستند لا تطابق الإجمالي المعتمد؛ الطباعة والمشاركة متوقفتان.');
    const documentId = payload.document_id || settings.document_id || null;
    const documentNumber = settings.document_number || payload.document_number || '';
    const sourceState = String(settings.document_state || payload.document_state || '').toLowerCase();
    const suppliedStatus = String(settings.status || payload.status || '').toLowerCase();
    if ([sourceState, suppliedStatus].some((state) => ['cancelled', 'canceled', 'superseded'].includes(state)))
        throw billingError('DOCUMENT_NOT_SHAREABLE', 'المستند ملغى أو مستبدل؛ لا يمكن مشاركته كمستند ساري.');
    if (docType === 'invoice' && (!documentId || !documentNumber || sourceState !== 'issued' || finalTotal === null))
        throw billingError('ISSUED_INVOICE_REQUIRED', 'اطبع فاتورة صادرة من الزيارة المعتمدة، وليس مسودة أو رقمًا مؤقتًا.');
    const rawPaid = settings.totals?.paid ?? payload.payment?.paid;
    if (docType === 'invoice' && !hasMoney(rawPaid)) throw billingError('DOCUMENT_PAYMENTS_UNAVAILABLE');
    const paid = money(rawPaid ?? 0, 'المدفوع المخصص');
    if (docType === 'invoice' && paid > total) throw billingError('DOCUMENT_PAYMENT_MISMATCH');
    const remaining = money(Math.max(total - paid, 0));
    const status = docType !== 'invoice' ? 'draft' : remaining === 0 ? 'paid' : paid > 0 ? 'partial' : 'unpaid';
    return { docType, documentId, documentNumber, status, items, totals: { subtotal, discount, tax, total, paid, remaining } };
};
